import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";

import { LabDetailsModal, type LabDetailsModalRef } from "../components/LabDetailsModal";
import { AdminCreateLabModal } from "../components/admin-labs/AdminCreateLabModal";
import { AdminLabAuditorModal } from "../components/admin-labs/AdminLabAuditorModal";
import { AdminLabFilters } from "../components/admin-labs/AdminLabFilters";
import { AdminLabsList } from "../components/admin-labs/AdminLabsList";
import { PageHeader } from "../components/pages/PageHeader";
import { LabConfirmRemovalModal, type LabConfirmRemovalModalRef } from "../components/pages/about/LabConfirmRemovalModal";
import { Card } from "../components/ui/Card";
import { useAuth } from "../contexts/useAuth";
import { AuditorService } from "../services/auditorService";
import { InvitationService } from "../services/invitationService";
import type {
    AdminLabDirectoryResponse,
    AdminLabRow,
    CreateLabData,
} from "../services/labService";
import { LabService } from "../services/labService";
import type { OrganisationDetailsResponse } from "../services/organisationService";
import type { LabType } from "../types/lab";

interface AdminLabsPageProps {
  directory: AdminLabDirectoryResponse;
  organisation: OrganisationDetailsResponse;
}

type VisibleStatusFilter =
  | "all"
  | AdminLabDirectoryResponse["filter_options"]["certification_statuses"][number]["value"];

export const AdminLabsPage = ({ directory, organisation }: AdminLabsPageProps) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<VisibleStatusFilter>("all");
  const [selectedLabType, setSelectedLabType] = useState<LabType | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreatingLab, setIsCreatingLab] = useState(false);
  const [busyLabIds, setBusyLabIds] = useState<Record<string, boolean>>({});
  const [selectedLabForDetails, setSelectedLabForDetails] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [selectedLabForAuditor, setSelectedLabForAuditor] =
    useState<AdminLabRow | null>(null);
  const [labPendingRemoval, setLabPendingRemoval] = useState<AdminLabRow | null>(null);

  const selectedCertification = selectedLabForAuditor?.current_certification ?? null;
  const selectedCertificationId = selectedCertification?.id ?? null;

  const labDetailsModalRef = useRef<LabDetailsModalRef>(null);
  const removalConfirmModalRef = useRef<LabConfirmRemovalModalRef>(null);

  useEffect(() => {
    if (selectedLabForDetails) {
      labDetailsModalRef.current?.showModal();
    }
  }, [selectedLabForDetails]);

  useEffect(() => {
    if (labPendingRemoval) {
      removalConfirmModalRef.current?.showModal();
    }
  }, [labPendingRemoval]);

  const showTypeFilters = directory.summary.supported_lab_types.length > 1;
  const hasActiveFilters =
    searchTerm.trim().length > 0 ||
    selectedStatus !== "all" ||
    selectedLabType !== null;

  const eligibleAuditorsQuery = useQuery({
    queryKey: ["auditors:eligible", selectedCertificationId],
    queryFn: async () => {
      if (!selectedCertificationId) {
        throw new Error("Certification ID is required");
      }

      return AuditorService.listEligibleAuditors(selectedCertificationId);
    },
    enabled: Boolean(selectedCertificationId),
  });

  const filteredLabs = directory.labs.filter((lab) => {
    const query = searchTerm.trim().toLowerCase();
    const championText = [lab.champion?.name, lab.champion?.email]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const matchesSearch =
      query.length === 0 ||
      lab.name.toLowerCase().includes(query) ||
      championText.includes(query);

    const matchesStatus =
      selectedStatus === "all" ||
      lab.current_certification?.workflow_status === selectedStatus;

    const matchesType = selectedLabType === null || lab.type === selectedLabType;

    return matchesSearch && matchesStatus && matchesType;
  });

  const actorName =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    user?.email ||
    "Organisation member";

  const summaryText =
    directory.summary.lab_limit === 0
      ? "No lab limit"
      : `${directory.summary.total_labs} / ${directory.summary.lab_limit} labs created`;

  const setLabBusy = (labId: string, isBusy: boolean) => {
    setBusyLabIds((prev) => {
      if (isBusy) {
        return { ...prev, [labId]: true };
      }

      const next = { ...prev };
      delete next[labId];
      return next;
    });
  };

  const invalidateAdminLabData = async (includeOrganisation: boolean) => {
    const tasks = [
      queryClient.invalidateQueries({ queryKey: ["admin-labs", organisation.id] }),
    ];

    if (includeOrganisation) {
      tasks.push(
        queryClient.invalidateQueries({ queryKey: ["organisation", organisation.id] }),
      );
    }

    await Promise.all(tasks);
  };

  const assignAuditorMutation = useMutation<
    void,
    Error,
    { certificationId: number; auditorId: string; auditorName: string; labName: string }
  >({
    mutationFn: async ({ certificationId, auditorId }) => {
      await AuditorService.assignAuditor(certificationId, auditorId);
    },
    onSuccess: async (_data, variables) => {
      toast.success(`${variables.auditorName} assigned to ${variables.labName}.`);
      setSelectedLabForAuditor(null);
      await Promise.all([
        invalidateAdminLabData(false),
        queryClient.invalidateQueries({
          queryKey: ["auditors:eligible", variables.certificationId],
        }),
      ]);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to assign auditor");
    },
  });

  const removeAuditorMutation = useMutation<
    void,
    Error,
    { certificationId: number; labName: string }
  >({
    mutationFn: async ({ certificationId }) => {
      await AuditorService.removeAuditor(certificationId);
    },
    onSuccess: async (_data, variables) => {
      toast.success(`Auditor removed from ${variables.labName}.`);
      setSelectedLabForAuditor(null);
      await invalidateAdminLabData(false);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to remove auditor");
    },
  });

  const inviteAuditorMutation = useMutation<
    { status: "sent" | "exists" | "error"; message?: string },
    Error,
    {
      certificationId: number;
      email: string;
      labName: string;
      previousAuditorName?: string;
    }
  >({
    mutationFn: async ({ certificationId, email }) => {
      const response = await InvitationService.sendUserInvitationForCertification(
        email,
        certificationId,
      );
      const result = response.results[0];
      if (!result || result.status === "error") {
        throw new Error(result?.message || "Failed to invite auditor");
      }
      return result;
    },
    onSuccess: async (result, variables) => {
      const successMessage =
        result.status === "exists"
          ? `Linked the existing organisation invite for ${variables.email} to ${variables.labName}.`
          : `Sent an organisation invite for ${variables.email} to ${variables.labName}.`;
      const reassignmentMessage = variables.previousAuditorName
        ? ` ${variables.previousAuditorName} was unassigned immediately.`
        : "";

      toast.success(
        `${successMessage}${reassignmentMessage}`,
      );
      await invalidateAdminLabData(false);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to invite auditor");
    },
  });

  const clearPendingInviteMutation = useMutation<
    void,
    Error,
    { certificationId: number; labName: string }
  >({
    mutationFn: async ({ certificationId }) => {
      await AuditorService.clearPendingInvite(certificationId);
    },
    onSuccess: async (_data, variables) => {
      toast.success(`Cleared the pending invite for ${variables.labName}.`);
      await invalidateAdminLabData(false);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to clear pending invite");
    },
  });

  const handleCreateLab = async (formData: CreateLabData): Promise<void> => {
    try {
      setIsCreatingLab(true);
      const result = await LabService.createLab(formData);
      toast.success(result.message);
      setIsCreateModalOpen(false);
      await invalidateAdminLabData(true);
    } catch (error) {
      throw error instanceof Error ? error : new Error("Failed to create lab");
    } finally {
      setIsCreatingLab(false);
    }
  };

  const handleJoinLab = async (labId: string): Promise<void> => {
    try {
      setLabBusy(labId, true);
      const result = await LabService.joinLab(labId);
      toast.success(result.message);
      await invalidateAdminLabData(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to join lab";
      toast.error(message);
    } finally {
      setLabBusy(labId, false);
    }
  };

  const handleLeaveLab = async (labId: string): Promise<void> => {
    try {
      setLabBusy(labId, true);
      const result = await LabService.leaveLab(labId);
      toast.success(result.message);
      await invalidateAdminLabData(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to leave lab";
      toast.error(message);
    } finally {
      setLabBusy(labId, false);
    }
  };

  const handleConfirmRemoveLab = async (): Promise<void> => {
    if (!labPendingRemoval) {
      return;
    }

    try {
      setLabBusy(labPendingRemoval.id, true);
      const result = await LabService.deleteLab(labPendingRemoval.id);
      toast.success(result.message);
      setLabPendingRemoval(null);
      await invalidateAdminLabData(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to remove lab";
      toast.error(message);
      throw error instanceof Error ? error : new Error(message);
    } finally {
      if (labPendingRemoval) {
        setLabBusy(labPendingRemoval.id, false);
      }
    }
  };

  return (
    <div className="l0-layout space-y-6">
      <PageHeader
        pageTitle="Labs"
        organisationName={organisation.name}
        adminGuidePath="/admin-guide"
      >
        <p className="max-w-2xl text-sm text-base-content/70">
          Create labs, manage lab membership, and track certification handoff from one place.
        </p>
      </PageHeader>

      <Card variant="primary">
        <div className="card-body gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-base-content/70">{summaryText}</p>
            {!directory.summary.can_create_lab && (
              <p className="text-sm text-error">Lab limit reached</p>
            )}
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setIsCreateModalOpen(true)}
            disabled={!directory.summary.can_create_lab}
          >
            Create Lab
          </button>
        </div>
      </Card>

      <AdminLabFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedStatus={selectedStatus}
        onStatusChange={setSelectedStatus}
        selectedLabType={selectedLabType}
        onLabTypeChange={setSelectedLabType}
        showTypeFilters={showTypeFilters}
        availableLabTypes={directory.filter_options.lab_types}
        statusOptions={directory.filter_options.certification_statuses}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={() => {
          setSearchTerm("");
          setSelectedStatus("all");
          setSelectedLabType(null);
        }}
      />

      <Card variant="primary">
        <div className="card-body">
          {directory.labs.length === 0 ? (
            <div className="rounded-lg border border-dashed border-[#eaddc9] bg-white/60 px-6 py-12 text-center">
              <h2 className="text-xl font-semibold">No labs yet</h2>
              <p className="mt-2 text-base-content/70">
                Create your first lab to start onboarding teams and tracking certification progress.
              </p>
              <button
                type="button"
                className="btn btn-primary mt-6"
                onClick={() => setIsCreateModalOpen(true)}
                disabled={!directory.summary.can_create_lab}
              >
                Create Lab
              </button>
            </div>
          ) : filteredLabs.length === 0 ? (
            <div className="rounded-lg border border-dashed border-[#eaddc9] bg-white/60 px-6 py-12 text-center">
              <h2 className="text-xl font-semibold">No labs match these filters</h2>
              <p className="mt-2 text-base-content/70">Try clearing one or more filters.</p>
              <button
                type="button"
                className="btn btn-outline mt-6"
                onClick={() => {
                  setSearchTerm("");
                  setSelectedStatus("all");
                  setSelectedLabType(null);
                }}
              >
                Clear filters
              </button>
            </div>
          ) : (
            <AdminLabsList
              labs={filteredLabs}
              showTypeBadge={showTypeFilters}
              busyLabIds={busyLabIds}
              onOpenMembers={(lab) => setSelectedLabForDetails({ id: lab.id, name: lab.name })}
              onManageAuditor={(lab) => setSelectedLabForAuditor(lab)}
              onJoin={handleJoinLab}
              onLeave={handleLeaveLab}
              onRemove={(lab) => setLabPendingRemoval(lab)}
            />
          )}
        </div>
      </Card>

      <AdminCreateLabModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateLab}
        isLoading={isCreatingLab}
        organisation={organisation}
        availableLabTypes={directory.summary.supported_lab_types}
      />

      <LabConfirmRemovalModal
        ref={removalConfirmModalRef}
        handleRemoveLab={handleConfirmRemoveLab}
        handleCancel={() => setLabPendingRemoval(null)}
      />

      {selectedLabForDetails && (
        <LabDetailsModal
          ref={labDetailsModalRef}
          labId={selectedLabForDetails.id}
          labName={selectedLabForDetails.name}
          isAdmin={true}
          onClose={() => setSelectedLabForDetails(null)}
          onMemberRemoved={() => {
            void invalidateAdminLabData(false);
          }}
          actorId={user?.id ?? null}
          actorName={actorName}
          organisationId={user?.organisation_id ?? null}
        />
      )}

      {selectedLabForAuditor && selectedCertification && (
        <AdminLabAuditorModal
          lab={selectedLabForAuditor}
          allowedEmailDomains={organisation.email_domains ?? []}
          eligibleAuditors={eligibleAuditorsQuery.data ?? []}
          isOpen={true}
          isLoadingEligible={eligibleAuditorsQuery.isLoading}
          eligibleError={
            eligibleAuditorsQuery.isError
              ? eligibleAuditorsQuery.error instanceof Error
                ? eligibleAuditorsQuery.error.message
                : "Failed to load eligible auditors"
              : null
          }
          isAssigning={assignAuditorMutation.isPending}
          isRemoving={removeAuditorMutation.isPending}
          isInviting={inviteAuditorMutation.isPending}
          isClearingPendingInvite={clearPendingInviteMutation.isPending}
          onAssign={(auditorId) => {
            const selectedAuditor = eligibleAuditorsQuery.data?.find(
              (auditor) => auditor.user_id === auditorId,
            );
            const auditorName = selectedAuditor
              ? [selectedAuditor.first_name, selectedAuditor.last_name]
                  .filter(Boolean)
                  .join(" ") || selectedAuditor.email
              : "Selected auditor";

            assignAuditorMutation.mutate({
              certificationId: selectedCertification.id,
              auditorId,
              auditorName,
              labName: selectedLabForAuditor.name,
            });
          }}
          onRemove={() => {
            removeAuditorMutation.mutate({
              certificationId: selectedCertification.id,
              labName: selectedLabForAuditor.name,
            });
          }}
          onInvite={(email) => {
            const previousAuditorName =
              selectedCertification.auditor?.name ||
              selectedCertification.auditor?.email ||
              undefined;

            inviteAuditorMutation.mutate({
              certificationId: selectedCertification.id,
              email: email.trim().toLowerCase(),
              labName: selectedLabForAuditor.name,
              previousAuditorName,
            });
          }}
          onClearPendingInvite={() => {
            clearPendingInviteMutation.mutate({
              certificationId: selectedCertification.id,
              labName: selectedLabForAuditor.name,
            });
          }}
          onClose={() => {
            if (
              assignAuditorMutation.isPending ||
              removeAuditorMutation.isPending ||
              inviteAuditorMutation.isPending ||
              clearPendingInviteMutation.isPending
            ) {
              return;
            }

            setSelectedLabForAuditor(null);
          }}
        />
      )}
    </div>
  );
};